<script setup lang="ts">
import { computed, watch } from 'vue';
import { useGroupFilterVisible } from '@/composables/useGroupFilterVisible';

interface Props {
  modelValue: number[];
  label?: string;
  variant?: 'underlined' | 'outlined' | 'plain' | 'solo' | 'filled';
  density?: 'default' | 'comfortable' | 'compact';
  chips?: boolean;
  maxVisibleChips?: number;
  hideDetails?: boolean | 'auto';
}

interface Emits {
  (e: 'update:modelValue', value: number[]): void;
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Grupos',
  variant: 'underlined',
  density: undefined,
  chips: true,
  maxVisibleChips: 1,
  hideDetails: false,
});

const emit = defineEmits<Emits>();

const { shouldShow, groups, groupsEnabled } = useGroupFilterVisible();

// Drop persisted IDs that the user can no longer see (feature disabled, access
// revoked, deleted group, or cross-company localStorage leakage). Without this
// the chips render blank and the API silently filters by a stale group.
watch([groups, groupsEnabled], ([loaded, enabled]) => {
  if (!enabled) {
    if (props.modelValue.length) emit('update:modelValue', []);
    return;
  }
  if (!loaded) return;
  const valid = new Set(loaded.map(g => g.id));
  const cleaned = props.modelValue.filter(id => valid.has(id));
  if (cleaned.length !== props.modelValue.length) {
    emit('update:modelValue', cleaned);
  }
}, { immediate: true });

const items = computed(() =>
  (groups.value ?? []).map(g => ({ value: g.id, title: g.name })),
);

const internalValue = computed({
  get: () => props.modelValue,
  set: (value: number[]) => emit('update:modelValue', value),
});

function nameFor(id: number): string {
  return items.value.find(i => i.value === id)?.title ?? '';
}

const summary = computed(() => {
  const count = props.modelValue?.length ?? 0;
  if (count === 0) return '';
  if (count === 1) return nameFor(props.modelValue[0]);

  return `${count} grupos`;
});

const selectedCount = computed(() => props.modelValue?.length ?? 0);

function removeItem(value: number) {
  emit('update:modelValue', props.modelValue.filter(v => v !== value));
}
</script>

<template>
  <v-autocomplete
    v-if="shouldShow"
    v-model="internalValue"
    :items="items"
    :label="label"
    :density="density"
    :variant="variant"
    :hide-details="hideDetails"
    multiple
    clearable
    single-line
  >
    <template #selection="{ item, index }">
      <v-chip
        v-if="chips && index < maxVisibleChips"
        closable
        size="small"
        @click:close.stop="removeItem(item.value as number)"
      >
        {{ item.title }}
      </v-chip>
      <span
        v-else-if="chips && index === maxVisibleChips"
        class="text-caption text-medium-emphasis ml-1"
      >
        +{{ selectedCount - maxVisibleChips }}
      </span>
      <span
        v-else-if="!chips && index === 0"
        class="text-body-2"
      >
        {{ summary }}
      </span>
    </template>
  </v-autocomplete>
</template>
